<script>
import baseMixin from '@/mixins/baseMixin'
import formatDate from '@/utils/format-date.js'
import formatTime from '@/utils/format-time.js'
import { mapGetters } from 'vuex'
import { ResolveOperations } from '@/constants/disputes/ResolveOperations'
import timesheetMixin from '@/mixins/timesheetMixin'
import { TimesheetChangeType } from '@/constants/disputes/TimesheetChangeType'

export default {
  name: 'DisputeDialogBookingDetails',
  mixins: [baseMixin, timesheetMixin],
  computed: {
    ...mapGetters({
      timesheet: 'disputes/activeTimesheet',
      resolveStatus: 'disputes/resolveStatus',
    }),
    bookingDate() {
      return this.timesheet?.startDate
        ? formatDate(this.timesheet.startDate)
        : this.$t('dispute.bookingDetails.unavailableDate')
    },
    startTime() {
      return formatTime(this.timesheet.startTime)
    },
    endTime() {
      return formatTime(this.timesheet.endTime)
    },
    candidateTimesheet() {
      return this.timesheet?.candidateTimesheet?.details
        ? this.timesheet.candidateTimesheet.details
        : {}
    },
    clientTimesheet() {
      return this.timesheet?.clientTimesheet?.details
        ? this.timesheet.clientTimesheet.details
        : {}
    },
    hasCandidateSubmittedTimesheet() {
      return this.timesheet.candidateHasSubmitted
    },
    hasClientSubmittedTimesheet() {
      return this.timesheet.clientHasSubmitted
    },
    mostRecentTimesheetSubmission() {
      return this.determineMostRecentTimesheetSubmissionOrder(this.timesheet)[0]
    },
    compMostRecentText() {
      switch (this.mostRecentTimesheetSubmission) {
        case ResolveOperations.CANDIDATE:
          return this.timesheet.candidate.name
        case ResolveOperations.CLIENT:
          return this.timesheet.clientName
        case ResolveOperations.ORIGINAL:
        default:
          return this.$t(
            'dispute.bookingDetails.mostRecentChangesTitleUnsubmitted'
          )
      }
    },
    hasSelectedMostRecentTimesheetSubmission() {
      return this.mostRecentTimesheetSubmission === this.resolveStatus
    },
    compButtonText() {
      if (!this.hasSelectedMostRecentTimesheetSubmission)
        return this.$t('dispute.bookingDetails.mostRecentSelectedUnselected')

      switch (this.mostRecentTimesheetSubmission) {
        case ResolveOperations.CANDIDATE:
          return this.$t('dispute.bookingDetails.mostRecentSelectedCandidate')
        case ResolveOperations.CLIENT:
          return this.$t('dispute.bookingDetails.mostRecentSelectedClient')
        case ResolveOperations.ORIGINAL:
        default:
          return this.$t('dispute.bookingDetails.mostRecentSelectedOriginal')
      }
    },
    compModifiedText() {
      let modifiedText = ''
      let isPositiveDifference = 0
      let differenceInMins = 0

      for (const change of this.retrieveModifiedTimesheetPayload(
        this.mostRecentTimesheetSubmission
      ).changes) {
        isPositiveDifference = change.differenceInMinutes >= 0
        differenceInMins = Math.abs(change.differenceInMinutes)

        switch (change.type) {
          case TimesheetChangeType.delta: {
            if (isPositiveDifference)
              modifiedText += this.$t(
                'dispute.bookingDetails.changes.posDelta',
                [differenceInMins]
              )
            else
              modifiedText += this.$t(
                'dispute.bookingDetails.changes.negDelta',
                [differenceInMins]
              )

            break
          }
          case TimesheetChangeType.shift: {
            if (isPositiveDifference)
              modifiedText += this.$t(
                'dispute.bookingDetails.changes.posShift',
                [differenceInMins]
              )
            else
              modifiedText += this.$t(
                'dispute.bookingDetails.changes.negShift',
                [differenceInMins]
              )

            break
          }
          case TimesheetChangeType.unpaidBreaks: {
            if (isPositiveDifference)
              modifiedText += this.$t(
                'dispute.bookingDetails.changes.posBreaks',
                [differenceInMins]
              )
            else
              modifiedText += this.$t(
                'dispute.bookingDetails.changes.negBreaks',
                [differenceInMins]
              )

            break
          }
        }
      }

      return modifiedText
    },
    compChangesText() {
      if (this.isAcceptOperation(this.mostRecentTimesheetSubmission))
        return this.$t('dispute.bookingDetails.changes.noChanges')
      if (this.isModifiedOperation(this.mostRecentTimesheetSubmission)) {
        return this.compModifiedText
      }
      if (this.isCancelOperation(this.mostRecentTimesheetSubmission)) {
        return this.$t('dispute.bookingDetails.changes.didNotHappen')
      }

      return ''
    },
  },
  methods: {
    selectMostRecentTimesheet() {
      const resolveStatus = this.hasSelectedMostRecentTimesheetSubmission
        ? null
        : this.mostRecentTimesheetSubmission

      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'resolveStatus',
        value: resolveStatus,
      })
    },
  },
}
</script>

<template>
  <v-alert
    :class="['rounded-lg mt-2 grey', isDarkMode ? 'darken-5' : 'lighten-5']"
  >
    <v-row :class="['align-center py-2', isDarkMode ? '' : 'black--text']">
      <v-col cols="12" md="4" sm="12">
        <h3
          class="font-weight-medium"
          style="position: relative; top: 6px; margin-bottom: 20px"
          >{{ bookingDate }}</h3
        >
        <p class="mb-0">{{ startTime }} - {{ endTime }}</p>
        <BaseLink
          :href="returnBookingUrl(timesheet.bookingId)"
          class="text-body-2"
          color="primary"
          :title="$t('general.link.viewBookingTooltip')"
          allow-insecure
          >#{{ timesheet.bookingId }}</BaseLink
        >
      </v-col>
      <v-col
        cols="12"
        md="8"
        sm="12"
        :class="[isLargeOrHigher ? 'text-right' : '']"
      >
        <v-btn
          depressed
          :outlined="hasSelectedMostRecentTimesheetSubmission"
          :style="
            hasSelectedMostRecentTimesheetSubmission
              ? { background: isDarkMode ? '' : 'white' }
              : null
          "
          color="primary"
          class="px-12"
          @click="selectMostRecentTimesheet"
        >
          <v-icon v-if="hasSelectedMostRecentTimesheetSubmission" left
            >mdi-check</v-icon
          >
          {{ compButtonText }}
        </v-btn>
        <p
          class="mt-2 mb-0"
          data-testid="dispute-dialog-booking-details-latest-submission"
        >
          {{ compMostRecentText }}
        </p>
        <p class="mb-0 text-body-2">
          {{ compChangesText }}
        </p>
      </v-col>
    </v-row>
  </v-alert>
</template>
